// src/ui/PanelBoundary.jsx
// ─── Isolation des pannes par panneau (§3, §17) ────────────────────────────
//
// L'application avait UNE frontière d'erreur, posée à la racine autour de
// `MainApp`. Conséquence : n'importe quelle exception, où qu'elle survienne,
// remplaçait l'écran ENTIER par la carte « Une erreur est survenue ». Un
// `undefined.name` dans la liste des conversations — un composant qui occupe
// 400 pixels — faisait donc disparaître le chat, les dossiers, l'agenda et la
// navigation. C'est ce que l'utilisateur décrit comme « la page devient
// blanche et l'application plante ».
//
// Une frontière racine reste nécessaire en dernier recours, mais elle est le
// mauvais outil pour une panne locale. Ce composant en pose une AUTOUR DE
// CHAQUE panneau : le reste de l'application continue de fonctionner, et le
// panneau fautif affiche un message réparable au lieu d'emporter tout l'écran.
//
// La cause première est corrigée par ailleurs (les accès sont gardés, et le
// backend renvoie toujours un `other` défini). Cette frontière est la ceinture
// après les bretelles : elle garantit qu'un défaut du même genre, ailleurs,
// ne produira plus jamais un écran blanc.

import { Component } from "react";

export class PanelBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) { return { hasError: true, error }; }

  componentDidCatch(error, info) {
    // Le nom du panneau est journalisé : sans lui, une pile de composants
    // minifiée ne dit pas quelle zone est tombée.
    console.error(`[PanelBoundary:${this.props.name || "?"}]`, error?.message, info?.componentStack);
    if (this.props.onError) { try { this.props.onError(error); } catch (e) { /* ignoré */ } }
  }

  componentDidUpdate(prev) {
    // Changer de vue doit réarmer la frontière : sinon un panneau resté en
    // erreur le demeure jusqu'au rechargement, même après que l'utilisateur a
    // navigué ailleurs et est revenu.
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div className="ws-alert error" role="alert" style={{ margin: ".6rem 0" }}>
        <span>
          {this.props.label || "Cette section n'a pas pu s'afficher."}
          {" "}Le reste de l'application reste utilisable.
        </span>
        <button type="button" className="ws-btn sm"
          onClick={() => this.setState({ hasError: false, error: null })}>
          Réessayer
        </button>
      </div>
    );
  }
}

/**
 * Enveloppe un composant sans toucher à son appelant :
 *   export default withPanelBoundary(MessagesPanel, "messages");
 */
export function withPanelBoundary(Wrapped, name, label) {
  const Guarded = (props) => (
    <PanelBoundary name={name} label={label} resetKey={props.resetKey}>
      <Wrapped {...props} />
    </PanelBoundary>
  );
  Guarded.displayName = `Guarded(${name || Wrapped.displayName || Wrapped.name || "Component"})`;
  return Guarded;
}

export default PanelBoundary;
